import { useState, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { FaClock } from "react-icons/fa";

export const parseTimeString = (value) => {
  if (!value || typeof value !== "string") return null;
  const match = value.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;
  let hour = parseInt(match[1], 10);
  const minute = parseInt(match[2], 10);
  if (Number.isNaN(hour) || Number.isNaN(minute)) return null;
  hour = Math.min(Math.max(hour, 0), 23);
  return { hour, minute };
};

const to24 = (hour12, minute, period) => {
  let hour = hour12 % 12;
  if (period === "PM") hour += 12;
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
};

const formatDisplay = (hour24, minute) => {
  const period = hour24 >= 12 ? "PM" : "AM";
  let hour12 = hour24 % 12;
  if (hour12 === 0) hour12 = 12;
  return `${String(hour12).padStart(2, "0")}:${String(minute).padStart(2, "0")} ${period}`;
};

const HOURS = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTES = Array.from({ length: 60 }, (_, i) => i);

export default function TimePicker({
  value,
  onChange,
  placeholder = "--:-- --",
  className = "",
  disabled = false,
}) {
  const parsed = parseTimeString(value);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });

  const wrapperRef = useRef(null);
  const btnRef = useRef(null);
  const panelRef = useRef(null);

  const hour24 = parsed ? parsed.hour : null;
  const minute = parsed ? parsed.minute : null;
  const period = hour24 !== null ? (hour24 >= 12 ? "PM" : "AM") : "AM";
  const hour12 = hour24 !== null ? (hour24 % 12 === 0 ? 12 : hour24 % 12) : null;

  const updatePosition = useCallback(() => {
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) return;
    setCoords({ top: rect.bottom + window.scrollY + 6, left: rect.left + window.scrollX });
  }, []);

  useLayoutEffect(() => {
    if (open) updatePosition();
  }, [open, updatePosition]);

  const handleToggle = () => {
    if (!open) updatePosition();
    setOpen((prev) => !prev);
  };

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      const clickedBtn = wrapperRef.current?.contains(e.target);
      const clickedPanel = panelRef.current?.contains(e.target);
      if (!clickedBtn && !clickedPanel) setOpen(false);
    };
    const handleEscape = (e) => { if (e.key === "Escape") setOpen(false); };

    document.addEventListener("mousedown", handleClickOutside, true);
    document.addEventListener("keydown", handleEscape);
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside, true);
      document.removeEventListener("keydown", handleEscape);
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open, updatePosition]);

  const pick = (nextHour12, nextMinute, nextPeriod) => {
    const h = nextHour12 ?? hour12 ?? 9;
    const m = nextMinute ?? minute ?? 0;
    const p = nextPeriod ?? period;
    onChange(to24(h, m, p));
  };

  return (
    <div className="time-picker-wrap" ref={wrapperRef}>
      <button
        type="button"
        ref={btnRef}
        className={`time-picker-btn ${className}`}
        disabled={disabled}
        onClick={handleToggle}
      >
        <FaClock className="time-picker-icon" />
        {hour24 !== null ? (
          <span>{formatDisplay(hour24, minute)}</span>
        ) : (
          <span className="time-picker-placeholder">{placeholder}</span>
        )}
      </button>

      {open &&
        createPortal(
          <div
            className="time-picker-panel"
            ref={panelRef}
            style={{ position: "absolute", top: coords.top, left: coords.left }}
          >
            <div className="time-picker-col">
              <div className="time-picker-col-label">HH</div>
              <div className="time-picker-col-items">
                {HOURS.map((h) => (
                  <div
                    key={h}
                    className={`time-picker-item ${hour12 === h ? "selected" : ""}`}
                    onClick={() => pick(h, null, null)}
                  >
                    {String(h).padStart(2, "0")}
                  </div>
                ))}
              </div>
            </div>

            <div className="time-picker-divider">:</div>

            <div className="time-picker-col">
              <div className="time-picker-col-label">MM</div>
              <div className="time-picker-col-items">
                {MINUTES.map((m) => (
                  <div
                    key={m}
                    className={`time-picker-item ${minute === m ? "selected" : ""}`}
                    onClick={() => pick(null, m, null)}
                  >
                    {String(m).padStart(2, "0")}
                  </div>
                ))}
              </div>
            </div>

            <div className="time-picker-col time-picker-period-col">
              <div className="time-picker-col-label">&nbsp;</div>
              <div className="time-picker-col-items no-scroll">
                {["AM", "PM"].map((p) => (
                  <div
                    key={p}
                    className={`time-picker-item period ${period === p ? "selected" : ""}`}
                    onClick={() => pick(null, null, p)}
                  >
                    {p}
                  </div>
                ))}
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}